import { BellAlertIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { localized, useNotifications } from "../../context/NotificationsContext";
import "./notifications.scss";

// Must match the exit transition in notifications.scss.
const EXIT_MS = 280;

/**
 * One popup. The countdown is the progress bar's own CSS animation, so hover
 * (and keyboard focus) pause it for free and its end is the dismiss signal.
 */
function Popup({ popup, onDone }) {
  const { t, i18n } = useTranslation();
  const { markRead, openPanel, openTarget } = useNotifications();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => onDone(popup.key), EXIT_MS);
    return () => clearTimeout(timer);
  }, [leaving, onDone, popup.key]);

  const leave = () => setLeaving(true);

  // A stored event to its target; a broadcast to its own link when it has
  // one, else to the bell's list; a local notice just closes.
  const open = async () => {
    leave();

    if (popup.target) {
      if (await openTarget(popup)) return;
    } else {
      markRead(popup.id);
    }

    if (!popup.id && !popup.url) return;

    if (!popup.url) {
      openPanel();
      return;
    }
    const target = new URL(popup.url, window.location.origin);
    if (target.origin === window.location.origin) {
      navigate(`${target.pathname}${target.search}${target.hash}`);
    } else {
      window.open(target.href, "_blank", "noopener");
    }
  };

  return (
    <div className={`np-slot ${leaving ? "is-leaving" : ""}`}>
      <div className="np-slot__inner">
        <div
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              open();
            }
          }}
          className="np-card pointer-events-auto relative flex cursor-pointer items-start gap-3 overflow-hidden rounded-card border border-line bg-surface p-4 pb-5 shadow-e2"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-wash text-accent-text">
            <BellAlertIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="break-words text-sm font-semibold text-ink">
              {localized(popup.title, i18n.language)}
            </p>
            <p className="mt-0.5 line-clamp-3 whitespace-pre-line break-words text-xs text-ink-2">
              {localized(popup.body, i18n.language)}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              leave();
            }}
            aria-label={t("notifications.close")}
            className="-m-1 shrink-0 rounded-md p-1 text-ink-2 transition-colors duration-fast hover:bg-raised hover:text-ink"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
          <span className="np-progress" onAnimationEnd={leave} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

/**
 * Foreground pushes, stacked in the bottom corner on the inline-end side —
 * `end-4` puts it bottom-right in English and bottom-left in Arabic off the
 * document's dir alone. Newest sits nearest the corner.
 */
export default function NotificationPopups() {
  const { popups, dismissPopup } = useNotifications();

  if (popups.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 end-4 z-[70] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col"
    >
      {popups.map((popup) => (
        <Popup key={popup.key} popup={popup} onDone={dismissPopup} />
      ))}
    </div>
  );
}
